import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const LOCALE_DZ = 'fr-DZ';

const numberFormatter = new Intl.NumberFormat(LOCALE_DZ, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat(LOCALE_DZ, {
  maximumFractionDigits: 0,
});

const compactFormatter = new Intl.NumberFormat(LOCALE_DZ, {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});

/** Format a number as Algerian dinar amount: "1 234 567,00 DA". */
export function formatDA(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '— DA';
  return `${numberFormatter.format(amount)} DA`;
}

/** Compact dinar amount: "1,2M DA". Useful for KPIs. */
export function formatDACompact(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '— DA';
  return `${compactFormatter.format(amount)} DA`;
}

/** Format an integer using fr-DZ thousands separator. */
export function formatNumberDZ(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return integerFormatter.format(value);
}

/** Format a percentage: 0.123 → "12,3 %". */
export function formatPercent(value: number | null | undefined, fractionDigits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '— %';
  return new Intl.NumberFormat(LOCALE_DZ, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Format a date as DD/MM/YYYY (Algeria standard). Accepts Date or ISO string. */
export function formatDateDZ(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return format(date, 'dd/MM/yyyy', { locale: fr });
}

/** Format a datetime as DD/MM/YYYY HH:mm (24h Algeria standard). */
export function formatDateTimeDZ(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
}

/** Long French date: "lundi 4 mai 2026". */
export function formatDateLongDZ(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return format(date, 'EEEE d MMMM yyyy', { locale: fr });
}

/** Initials from a full name: "Karim Benzema" → "KB". */
export function initials(fullName: string | null | undefined): string {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}
